import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { DataTable, Pagination } from 'grommet';
import { CircleAlert, Group, StatusGood } from 'grommet-icons';
import { ApiError, ClientService, TListUsersRequest, UserService } from '/src/api';
import {
  AddButton,
  Box,
  BoxProps,
  Card,
  CardBody,
  CardHeader,
  DataTableHeader,
  DataTableItem,
  Filters,
  LoadingSpinner,
  SearchInput,
  Text,
  UserInfoBox,
} from '/src/components';
import { useGlobalStore, useUserStore } from '/src/context';
import { useRestorableListState, useSyncDraft } from '/src/hooks/useRestorableListState';
import { ExternalRoles, TClientId, User } from '/src/lib/models';
import { toastMessages } from '/src/lib/toast';
import { EntityStatus, TResponseMetadata } from '/src/lib/types';
import { pxToRem } from '/src/utils';

type UserListQueryState = {
  page: number;
  search: string;
  role_id: number; // 0 = all
  is_active: number; // 0=inactive, 1=active, 2=all
};

const USER_LIST_DEFAULTS: UserListQueryState = {
  page: 1,
  search: '',
  role_id: 0,
  is_active: 2,
};

// Original defaults already represent "all" for every filter, so CLEARED === DEFAULTS
const USER_LIST_CLEARED: UserListQueryState = {
  ...USER_LIST_DEFAULTS,
};

export const UserList: React.FC<UserListProps> = (props) => {
  /* Props */
  const {
    id,
    title,
    clientId,
    isClientPage,
    showAddButton,
    showFilters,
    showSearchInput,
    isLoading: propIsLoading,
    users: propUsers,
    fetchUsers: propFetchUsers,
    metadata: propMetadata,
    currentPage: propCurrentPage,
    setCurrentPage: propSetCurrentPage,
    hideHeader,
    boxProps,
    surfaceId,
    standalone = false,
    mode = 'query',
  } = props;

  const isPropControlled = mode === 'controlled';

  /* Context */
  const userStore = useUserStore();
  const globalStore = useGlobalStore();
  const { t } = useTranslation();

  /* Restorable state */
  const restorableList = useRestorableListState<UserListQueryState>({
    surfaceId: surfaceId || 'user-list',
    defaults: USER_LIST_DEFAULTS,
    standalone,
  });

  const queryState = restorableList.state;
  const setQueryState = restorableList.setState;

  /* Draft state */
  const [draftSearch, setDraftSearch] = useState(queryState.search);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [draftFilters, setDraftFilters] = useState<Record<string, any>>({
    role_id: queryState.role_id,
    is_active: queryState.is_active,
  });

  /* Memos */
  const filteredRoleOptions = useMemo(
    () =>
      (!userStore.isInternalUser
        ? globalStore.roles.filter((role) => ExternalRoles.includes(role.id))
        : globalStore.roles
      ).map((role) => ({
        label: role.name,
        value: role.id,
      })),
    [globalStore.roles, userStore.isInternalUser]
  );

  /* Build the API request */
  const request: TListUsersRequest = useMemo(
    () => ({
      page: queryState.page,
      name: queryState.search || undefined,
      role_id: queryState.role_id || undefined,
      is_active: queryState.is_active === 1 ? true : queryState.is_active === 0 ? false : undefined,
    }),
    [queryState]
  );

  /* React Query */
  const {
    data: queryData,
    isLoading: queryIsLoading,
    isFetching,
    error: queryError,
  } = useQuery({
    queryKey: ['users', userStore.user?.id, clientId, request],
    queryFn: () => (clientId ? ClientService.listClientUsers({ ...request, id: clientId }) : UserService.list(request)),
    enabled: !isPropControlled && !!userStore.user,
    placeholderData: (prev) => prev,
  });

  const queryUsers = queryData?.data;
  const queryMetadata = queryData?.meta;

  /* Show toast on query error */
  useEffect(() => {
    if (queryError && !isPropControlled) {
      globalStore.handleApiError(queryError as ApiError, toastMessages.listUsers.error);
    }
  }, [queryError]);

  /* Resolved values */
  const _users = propUsers ?? queryUsers;
  const _metadata = propMetadata ?? queryMetadata;
  const _currentPage = propCurrentPage ?? queryState.page;
  const _isLoading = propIsLoading ?? (isPropControlled ? false : queryIsLoading || isFetching);
  const isRefetching = isFetching && !queryIsLoading;
  const hasUsers = useMemo(() => !!_users?.length, [_users]);

  /* Methods */
  const search = useCallback(
    (query?: string) => {
      setQueryState({ search: query || '', page: 1 });
    },
    [setQueryState]
  );

  const applyFilters = useCallback(() => {
    setQueryState({
      role_id: draftFilters.role_id,
      is_active: draftFilters.is_active,
      page: 1,
    });
  }, [setQueryState, draftFilters]);

  const clearFilters = useCallback(() => {
    setDraftFilters({ role_id: USER_LIST_CLEARED.role_id, is_active: USER_LIST_CLEARED.is_active });
    setDraftSearch('');
    setQueryState(USER_LIST_CLEARED);
  }, [setQueryState]);

  const handlePageChange = useCallback(
    (page: number) => {
      if (isPropControlled) {
        propSetCurrentPage?.(page);
        propFetchUsers?.(page);
      } else {
        setQueryState({ page });
      }
    },
    [isPropControlled, propSetCurrentPage, propFetchUsers, setQueryState]
  );

  /* Sync page from API response */
  useEffect(() => {
    if (queryMetadata && !isPropControlled && queryMetadata.current_page !== queryState.page) {
      setQueryState({ page: queryMetadata.current_page });
    }
  }, [queryMetadata?.current_page]);

  /* Sync drafts when queryState changes */
  useSyncDraft(queryState, setDraftSearch, (s) => s.search);
  useSyncDraft(queryState, setDraftFilters, (s) => ({ role_id: s.role_id, is_active: s.is_active }));

  /* Render */
  return (
    <Box id={id} direction="row" gap="medium" fill="horizontal">
      <Card {...boxProps}>
        {!hideHeader && (
          <CardHeader title={title || t('user:userList')} icon={<Group size="24px" color="brand" />}>
            {showSearchInput && (
              <SearchInput
                searchQuery={draftSearch}
                setSearchQuery={setDraftSearch}
                onSearch={search}
                isSearching={isRefetching}
                data-tour="user-search"
              />
            )}
            {showAddButton && (
              <AddButton
                label={t('user:addUser')}
                targetUrl={`/users/create${clientId ? `?client_id=${clientId}` : ''}`}
              />
            )}
          </CardHeader>
        )}
        <CardBody pad="none" gap="none">
          <Box elevation="small">
            {_isLoading && <LoadingSpinner />}
            {!_isLoading && (
              <DataTable
                pad={{ horizontal: '1.5rem' }}
                columns={[
                  {
                    property: 'id',
                    primary: true,
                    header: <DataTableHeader>{t('user:user')}</DataTableHeader>,
                    render: (user: User) => <UserInfoBox user={user} clientId={clientId} />,
                  },
                  {
                    property: isClientPage ? 'last_login_at' : 'clients',
                    header: <DataTableHeader>{isClientPage ? t('user:lastLogin') : t('user:company')}</DataTableHeader>,
                    render: (user: User) => (
                      <DataTableItem boxProps={{ align: 'center', height: pxToRem(92) }}>
                        {isClientPage ? (
                          user.last_login_at ? (
                            dayjs(user.last_login_at).format('MM/DD/YYYY @ hh:mm A')
                          ) : (
                            'Never'
                          )
                        ) : user.clients && user.clients.length > 0 ? (
                          <Box width="250px" overflow="hidden">
                            <Text truncate={true}>{user.clients.map((client) => client.name).join(', ')}</Text>
                          </Box>
                        ) : (
                          '-'
                        )}
                      </DataTableItem>
                    ),
                  },
                  {
                    property: 'role.name',
                    header: <DataTableHeader>{t('user:userRole')}</DataTableHeader>,
                    align: 'start',
                    render: (user: User) =>
                      user.role ? (
                        <Box
                          background="light-4"
                          pad={{ horizontal: '0.5rem', vertical: '0.25rem' }}
                          borderRadius="6px"
                          border={{ color: 'light-2', size: 'small' }}
                        >
                          <Text size="xsmall" weight={700} fontFamily="Lato, sans-serif">
                            {user.role?.name.split(' ').join(' - ')}
                          </Text>
                        </Box>
                      ) : undefined,
                  },
                  {
                    property: 'active',
                    header: <DataTableHeader>{t('common:status')}</DataTableHeader>,
                    render: (user: User) => (
                      <DataTableItem>
                        <Box direction="row" gap="xsmall">
                          <Box alignSelf="center">
                            {user.is_active ? <StatusGood color="green" size="16px" /> : <CircleAlert size="16px" />}
                          </Box>
                          <Text alignSelf="center" size="medium" fontFamily="Lato, sans-serif">
                            {user.is_active ? EntityStatus.Active : EntityStatus.Inactive}
                          </Text>
                        </Box>
                      </DataTableItem>
                    ),
                  },
                ]}
                data={_users}
                background={['light-6', 'white']}
                border={{ color: 'light-2', side: 'bottom', size: 'small' }}
              />
            )}
            {!hasUsers && !_isLoading && (
              <Box pad={{ horizontal: '1.5rem', vertical: '2rem' }} background="light-6" justify="center">
                <Text alignSelf="center" size="medium" fontFamily="Lato, sans-serif">
                  {t('user:noUsersFound')}
                </Text>
              </Box>
            )}
          </Box>
          {!!_metadata && !!_currentPage && (
            <Box pad="1.5rem" flex="grow">
              <Pagination
                alignSelf="center"
                size="small"
                page={_currentPage}
                step={_metadata?.per_page}
                numberItems={_metadata?.total}
                onChange={(e) => handlePageChange(e.page)}
              />
            </Box>
          )}
        </CardBody>
      </Card>
      {showFilters && filteredRoleOptions && (
        <Filters
          isFiltering={isRefetching}
          onSubmit={() => applyFilters()}
          onClear={() => clearFilters()}
          data-tour="user-filters"
          filters={[
            {
              label: t('user:userRole'),
              value: draftFilters.role_id,
              setValue: (value) => setDraftFilters({ ...draftFilters, role_id: value as number }),
              options: [{ label: t('common:all'), value: 0 }, ...filteredRoleOptions],
            },
            {
              label: t('user:userStatus'),
              value: draftFilters.is_active,
              setValue: (value) => setDraftFilters({ ...draftFilters, is_active: value as number }),
              options: [
                { label: t('common:all'), value: 2 },
                { label: t('common:active'), value: 1 },
                { label: t('user:inactive'), value: 0 },
              ],
            },
          ]}
        />
      )}
    </Box>
  );
};

export type UserListProps = {
  id?: string;
  clientId?: TClientId;
  title?: string;
  users?: User[];
  fetchUsers?: (page?: number) => void;
  metadata?: TResponseMetadata;
  isLoading?: boolean;
  showAddButton?: boolean;
  showFilters?: boolean;
  showSearchInput?: boolean;
  isClientPage?: boolean;
  hideHeader?: boolean;
  currentPage?: number;
  setCurrentPage?: (page: number) => void;
  boxProps?: BoxProps;
  surfaceId?: string;
  standalone?: boolean;
  mode?: 'query' | 'controlled';
};
